import type { Virtualizer } from '@tanstack/react-virtual';
import { useLayoutEffect, useMemo, useRef } from 'react';
import { useSwimlaneApiMethod, useSwimlanePrivateApi } from '../api';
import { useSwimlaneRootProps, useSwimlaneState } from '../context';
import type { IHeader, TItemID } from '../types';
import { buildCellId, parseCardId } from '../utils';
import { CELL_WRAPPER_PADDING, IMPLICIT_ROW_ID } from './constants';
import {
  type Extent,
  getCardExtent,
  getHorizontalAxis,
  getScrollToCenter,
  getStickyCoverX,
  getVerticalAxis,
  getVisibleBand,
  getVisibleFraction,
} from './geometry';
import { getGridOriginX, getGroupHeadersOffset } from './layoutMetrics';

const MIN_VISIBLE_FRACTION = 0.5;

// Without row headers only the implicit row renders, so other row ids have no position.
const findRowIndex = (rowHeaders: readonly IHeader[], rowID: string) => {
  if (rowHeaders.length === 0) return rowID === IMPLICIT_ROW_ID ? 0 : -1;
  return rowHeaders.findIndex((header) => header.id === rowID);
};

const isGroupHeader = (header: IHeader | undefined) =>
  header !== undefined && 'isGroupHeader' in header && header.isGroupHeader === true;

type CardLocation = { vertical: Extent; horizontal: Extent };

export function useSwimlaneCardNavigation({
  rowVirtualizer,
  columnVirtualizer,
  scrollContainer,
}: {
  rowVirtualizer: Virtualizer<HTMLDivElement, Element>;
  columnVirtualizer: Virtualizer<HTMLDivElement, Element>;
  scrollContainer: HTMLDivElement;
}) {
  const rootProps = useSwimlaneRootProps();
  const { getCellCardIDs, getCardHeight, hasCardDraft } = useSwimlaneState();
  const privateApiRef = useSwimlanePrivateApi();

  const layout = {
    rootProps,
    getCellCardIDs,
    getCardHeight,
    hasCardDraft,
    rowVirtualizer,
    columnVirtualizer,
    scrollContainer,
  };
  // Registered once; the methods read the latest layout through this ref.
  const latest = useRef(layout);
  useLayoutEffect(() => {
    latest.current = layout;
  });

  const api = useMemo(() => {
    const locateCards = (itemID: TItemID): CardLocation[] => {
      const { rootProps: props, ...current } = latest.current;
      const { rowHeaders, columnHeaders, cells, readOnly } = props;
      const locations: CardLocation[] = [];

      for (const [rowID, row] of cells) {
        for (const [columnID, itemIDs] of row) {
          if (!itemIDs.includes(itemID)) continue;

          const normalizedRowID = rowID ?? IMPLICIT_ROW_ID;
          const rowIndex = findRowIndex(rowHeaders, normalizedRowID);
          const columnIndex = columnHeaders.findIndex((header) => header.id === columnID);
          const cellId = buildCellId({ rowID: normalizedRowID, columnID });
          const cardIDs = current.getCellCardIDs(cellId);
          const cardIndex = cardIDs.findIndex((cardId) => parseCardId(cardId).itemID === itemID);
          const rowItem = current.rowVirtualizer.measurementsCache[rowIndex];
          const groupHeadersOffset = getGroupHeadersOffset(rowIndex, (index) =>
            isGroupHeader(rowHeaders[index])
              ? current.rowVirtualizer.options.estimateSize(index)
              : undefined,
          );
          const column = current.columnVirtualizer.measurementsCache[columnIndex];
          if (rowIndex === -1 || columnIndex === -1 || cardIndex === -1 || !rowItem || !column) {
            continue;
          }

          locations.push({
            vertical: getCardExtent({
              rowStart: rowItem.start - groupHeadersOffset,
              slots: cardIDs.map((cardId) => ({
                cardHeight: current.getCardHeight(cardId),
                hasCardDraftBelow: !readOnly && current.hasCardDraft(cardId),
              })),
              cardIndex,
              hasCellDraft: !readOnly && current.hasCardDraft(cellId),
            }),
            horizontal: {
              start: column.start + CELL_WRAPPER_PADDING.value,
              end: column.start + column.size - CELL_WRAPPER_PADDING.value,
            },
          });
        }
      }
      return locations;
    };

    const getAxes = () => {
      const { rootProps: props, scrollContainer: container } = latest.current;
      const isHeadersSticky = Boolean(props.isHeadersSticky);
      const leadingColumns = {
        hasRowHeaders: props.rowHeaders.length > 0,
        hasGroupHeaders: props.rowHeaders.some(isGroupHeader),
        hasAxisLabelRow: typeof props.rowAxisLabelText === 'string',
        rowHeaderOrientation: props.rowHeaderOrientation,
        rowHeadersWidth: props.rowHeadersWidth,
      };
      return {
        vertical: getVerticalAxis({
          viewportHeight: container.clientHeight,
          hasAxisLabelColumn: typeof props.columnAxisLabelText === 'string',
          isHeadersSticky,
        }),
        horizontal: getHorizontalAxis({
          viewportWidth: container.clientWidth,
          gridOriginX: getGridOriginX(leadingColumns),
          stickyCoverX: getStickyCoverX(leadingColumns),
          isHeadersSticky,
        }),
      };
    };

    // Live scroll: the virtualizers' cached offsets lag jumps.
    const isLocationVisible = (location: CardLocation) => {
      const { scrollTop, scrollLeft } = latest.current.scrollContainer;
      const axes = getAxes();
      const visibleFraction =
        getVisibleFraction(getVisibleBand(axes.vertical, scrollTop), location.vertical) *
        getVisibleFraction(getVisibleBand(axes.horizontal, scrollLeft), location.horizontal);
      return visibleFraction >= MIN_VISIBLE_FRACTION;
    };

    // Registered methods outlive the component; a detached board offers no jump.
    const isDetached = () => !latest.current.scrollContainer.isConnected;

    return {
      isCardVisible: (itemID: TItemID) => {
        if (isDetached()) return true;
        const locations = locateCards(itemID);
        return locations.length === 0 ? undefined : locations.some(isLocationVisible);
      },
      scrollToCard: (itemID: TItemID) => {
        if (isDetached()) return;
        const locations = locateCards(itemID);
        const target = locations[0];
        if (!target || locations.some(isLocationVisible)) return;

        const axes = getAxes();
        // One scroll: separate per-axis ones let new cards reset the vertical offset.
        latest.current.scrollContainer.scrollTo({
          top: getScrollToCenter(axes.vertical, target.vertical),
          left: getScrollToCenter(axes.horizontal, target.horizontal),
        });
      },
    };
  }, []);

  useSwimlaneApiMethod(privateApiRef, api, 'public');
}
